// shell/Backdrop/resolveTier.ts — resolve the concrete background render tier.
import type { BackdropKey } from '@/core/models';

/** Session latch: a previous session's frame watchdog (slice B) demotes flaky
 *  devices here so the NEXT visit starts conservative. Auto-tier reads it. */
const LATCH_KEY = 'truxsocial:backdropLatch';

export function readBackdropLatch(): 'low' | null {
  try {
    return localStorage.getItem(LATCH_KEY) === 'low' ? 'low' : null;
  } catch {
    return null;
  }
}

export function writeBackdropLatch(value: 'low' | null): void {
  try {
    if (value === null) localStorage.removeItem(LATCH_KEY);
    else localStorage.setItem(LATCH_KEY, value);
  } catch {
    // storage unavailable — latch is best-effort
  }
}

/** 'auto' uses a cheap device-capability heuristic (an FPS-probe upgrade is a
 *  later slice); explicit tiers pass straight through. */
export function resolveTier(pref: BackdropKey): 'high' | 'low' | 'off' {
  if (pref === 'off') return 'off';
  if (pref === 'high' || pref === 'low') return pref;
  // auto: a previous session's watchdog latch wins before the heuristics.
  if (readBackdropLatch() === 'low') return 'low';
  if (typeof navigator === 'undefined') return 'high';
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };
  if (nav.connection?.saveData) return 'low';
  const mem = nav.deviceMemory ?? 4;
  const cores = nav.hardwareConcurrency ?? 4;
  return mem >= 4 && cores >= 4 ? 'high' : 'low';
}
